import React, { useCallback, useMemo } from 'react'
import { connect } from 'react-redux'

import MultiSaveButton from './MultiSaveButton'
import { getTripImageFromCheckoutItems } from './utils'
import {
  AllCheckoutItemViews,
  getAllItemViews,
} from '../../../checkout/selectors/view/generic'

import { useAppSelector } from 'hooks/reduxHooks'
import useResponsiveSelector from 'hooks/useResponsiveSelector'
import { EmptyArray } from 'lib/array/arrayUtils'
import { getCartId } from 'selectors/checkoutSelectors'
import { BookmarkResponse } from 'tripPlanner/api/bookmark/types'
import { useEditableTrips } from 'tripPlanner/hooks/api'
import useBookmarkSnackbarHandlers from 'tripPlanner/hooks/bookmarks/useSavedItemSnackbarHandlers'
import useNewTripNameFromOffer from 'tripPlanner/hooks/useNewTripNameFromOffer'
import { BasicTrip } from 'tripPlanner/types/common'
import { createBookmarkPayloadsFromCheckout } from 'tripPlanner/utils/bookmarks/checkout'
import {
  createTripItemKeysFromBookmarkPayloads,
  getTripItemIdsInCheckout,
  tripHasItemKeys,
} from 'tripPlanner/utils/bookmarks/common'

interface Props {
  checkoutItemViews: AllCheckoutItemViews
  currentTripId?: string
}

function CheckoutSaveButton({ checkoutItemViews, currentTripId }: Props) {
  const { data: editableTrips } = useEditableTrips()
  const trips = editableTrips ?? EmptyArray
  const firstOffer = checkoutItemViews.data.accommodationItemsView.data
    .map((view) => view.offer)
    .find((offer) => offer?.type !== 'cruise')

  const cartId = useAppSelector(getCartId)

  const [payloads, keys] = useMemo(() => {
    try {
      const payloads = createBookmarkPayloadsFromCheckout(
        checkoutItemViews,
        cartId,
      )
      const keys = createTripItemKeysFromBookmarkPayloads(payloads)
      return [payloads, keys]
    } catch {
      return [[], []]
    }
  }, [checkoutItemViews, cartId])

  const trip = useMemo(
    () =>
      currentTripId ?
        trips.find((t) => t.id === currentTripId) :
        trips.find((trip) => tripHasItemKeys(trip, keys)),
    [currentTripId, keys, trips],
  )

  const newTripName = useNewTripNameFromOffer(firstOffer)

  const savedItemIds = useMemo(
    () => getTripItemIdsInCheckout(keys, trip?.plannedIdSets ?? []),
    [trip, keys],
  )

  const newTripImageId = useMemo(
    () => getTripImageFromCheckoutItems(checkoutItemViews, trips),
    [checkoutItemViews, trips],
  )

  const {
    showSaveSuccessSnackbar,
    showSaveErrorSnackbar,
    showRemoveSuccessSnackbar,
    showRemoveErrorSnackbar,
  } = useBookmarkSnackbarHandlers()

  const onSaveSuccess = useCallback(
    (data: BookmarkResponse['result'], trip: BasicTrip) => {
      showSaveSuccessSnackbar(trip.id, trip.name, data.created[0].id)
    },
    [showSaveSuccessSnackbar],
  )

  const onRemoveSuccess = useCallback(
    (trip: BasicTrip) => {
      showRemoveSuccessSnackbar(trip.id, trip.name)
    },
    [showRemoveSuccessSnackbar],
  )

  const saveLabel = useResponsiveSelector({
    mobile: 'Save',
    tablet: 'Save',
    desktop: 'Save to trip',
    largeDesktop: 'Save to trip',
  })

  if (payloads.length === 0) {
    return null
  }

  return (
    <MultiSaveButton
      focusedTrip={trip}
      newTripImageId={newTripImageId}
      newTripName={newTripName}
      keys={keys}
      onRemoveError={showRemoveErrorSnackbar}
      onRemoveSuccess={onRemoveSuccess}
      onSaveError={showSaveErrorSnackbar}
      onSaveSuccess={onSaveSuccess}
      payloads={payloads}
      saveLabel={saveLabel}
      savedItemIds={savedItemIds}
      trips={trips}
    />
  )
}

function mapStateToProps(state: App.State) {
  return {
    checkoutItemViews: getAllItemViews(state),
    currentTripId: state.tripPlanner.currentTripId,
  }
}

export default connect(mapStateToProps)(CheckoutSaveButton)
